import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
/* eslint-disable no-shadow */
/* eslint-disable no-underscore-dangle */
import {
  Button,
  Dialog,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import { createBrowserHistory } from 'history';
import PropTypes from 'prop-types';
import * as React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { AutoAwesome } from '@mui/icons-material';
import taskImage from '../../images/unused-undraw-illustrations/undraw_next-tasks_y3rm.svg';
import {
  setGlobalModals,
  setSelectedBarnHorse,
} from '../../redux/app/actions';
import {
  SCHEDULE_MODAL_TYPE,
  SCHEDULE_MODAL_TYPE_DIALOG,
  SET_GLOABL_MODALS_CONFIG,
  STATUS_CHANGE_MODAL_TYPE,
  TASK_MODAL_TYPE,
  TASK_MODAL_TYPE_DIALOG,
} from '../../redux/app/types';
import theme from '../../utils/theme';
import ChangeStatusDialog from '../ChangeStatusDialog';
import HorsePopover from '../HorsePopover';
import { PlanningModeTaskOverview } from '../Planner/components/TaskCalendarOverview/components/PlanningModeTaskOverview';
import AddEditEventScheduleModal from '../Schedule/AddEditEventScheduleModal';
import AddEditScheduleModal from '../Schedule/AddEditScheduleModal';
import TaskModal from '../TaskModal';

const drawerWidth = 450;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: { width: '100%' },
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: '0px',
  [theme.breakpoints.up('sm')]: { width: '0px' },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    height: '100%',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [{
      props: (
        { open },
      ) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    }, {
      props: (
        { open },
      ) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    }],
  }),
);

export const history = createBrowserHistory();

const GlobalModals = ({
  statusChange,
  task,
  horse,
  addEditSchedule,
}) => {
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const reminderFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // redux state
  const selectedBarnHorse = useSelector((state) => state.app.selectedBarnHorse);
  const openModalType = useSelector((state) => state.app.openModalType);
  const inlineDrawer = useSelector((state) => state.app.inlineDrawer);
  const modalCallback = useSelector((state) => state.app.modalCallback);
  const modalConfig = useSelector((state) => state.app.modalConfig);
  const selectedTask = useSelector((state) => state.event.selectedEvent);
  const selectedSchedule = useSelector((state) => state.schedule.schedule);
  const selectedScheduleEvent = useSelector((state) => state.schedule.event);
  const scheduleConfig = useSelector((state) => state.schedule.scheduleConfig);

  const handleResetGlobalModals = (disableDrawer = false) => {
    dispatch(setGlobalModals(null, null, null, null, { drawer: disableDrawer }, () => { }))
    dispatch(setSelectedBarnHorse(null));
  }

  React.useEffect(() => {
    // When page changes make sure any open modals are closed
    handleResetGlobalModals(inlineDrawer && pathname.startsWith('/tasks/planning'));
  }, [pathname]);

  const handleToggleQuestriSuggestions = () => {
    dispatch({
      type: SET_GLOABL_MODALS_CONFIG,
      modalConfig: {
        ...modalConfig,
        hideSuggestions: !modalConfig.hideSuggestions,
      },
    })
  }

  const isDialog = (openModalType === TASK_MODAL_TYPE_DIALOG || openModalType === SCHEDULE_MODAL_TYPE_DIALOG)
  const Component = isDialog
    ? Dialog
    : Drawer;
  const componentProps = isDialog
    ? {}
    : { sx: { paddingTop: '3rem' } }
  const reminderDialogHeight = reminderFullScreen
    ? '100%'
    : 'min(760px, calc(100dvh - 64px))';
  const reminderDialogProps = isDialog && modalConfig.reminderDialog
    ? {
      fullScreen: reminderFullScreen,
      fullWidth: true,
      maxWidth: 'sm',
      hideBackdrop: true,
      transitionDuration: 0,
      PaperProps: {
        sx: {
          borderRadius: 3,
          height: reminderDialogHeight,
        },
      },
    }
    : {};

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Component
        variant="permanent"
        open={openModalType !== null || !!selectedBarnHorse || (inlineDrawer && !fullScreen)}
        anchor="right"
        PaperProps={componentProps}
        {...reminderDialogProps}
      >
        {
          inlineDrawer && openModalType === null && !selectedBarnHorse && (
            <Box
              sx={{
                p: 1,
                display: 'flex',
                textWrap: 'auto',
                textAlign: 'center',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 1,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  backgroundColor: 'rgba(47,72,88,0.08)',
                  borderRadius: 4,
                  p: 1,
                }}
              >
                <Typography variant="h3" sx={{ fontSize: '2.5rem' }}>
                  Planning Mode
                </Typography>
                <Typography>
                  Select a horse, task, or calendar to
                  quickly plan a day, assign tasks, and
                  track billing transactions.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={taskImage}
                    alt="man selecting something"
                    style={{
                      width: 120,
                      display: 'block',
                      margin: 'auto',
                    }}
                  />
                  <Typography
                    fontWeight={300}
                    sx={{
                      fontSize: 13,
                      flex: 1,
                    }}
                  >
                    Questri will show you suggested tasks for each horse based
                    on your previous planning and history. After a few weeks
                    of planning you can expect more accurate suggestions.
                  </Typography>
                </Box>
                <Button
                  sx={{
                    margin: 'auto',
                    marginTop: 2,
                    width: '275px',
                  }}
                  size="small"
                  onClick={handleToggleQuestriSuggestions}
                  variant="contained"
                  color={modalConfig.hideSuggestions
                    ? 'primary'
                    : 'secondary'}
                  startIcon={<AutoAwesome sx={{ color: '#FF6600 !important' }} />}
                >{modalConfig.hideSuggestions
                  ? 'Show Suggested Tasks'
                  : 'Hide Suggested Tasks'}
                </Button>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                }}
              >
                <PlanningModeTaskOverview />
              </Box>
            </Box>
          )
        }
        {
          statusChange && selectedTask && openModalType === STATUS_CHANGE_MODAL_TYPE && (
            <ChangeStatusDialog
              open={openModalType === STATUS_CHANGE_MODAL_TYPE}
              handleClose={handleResetGlobalModals}
              selectedEvent={selectedTask}
            />
          )
        }
        {
          task && selectedTask && (openModalType === TASK_MODAL_TYPE || openModalType === TASK_MODAL_TYPE_DIALOG) && (
            <TaskModal
              navigate={navigate}
              handleClose={() => { handleResetGlobalModals(!!inlineDrawer); modalCallback(); }}
              open={openModalType === TASK_MODAL_TYPE}
            />
          )
        }
        {
          addEditSchedule && (openModalType === SCHEDULE_MODAL_TYPE || openModalType === SCHEDULE_MODAL_TYPE_DIALOG)
          && !scheduleConfig.locationId && (
            <AddEditScheduleModal
              open={openModalType === SCHEDULE_MODAL_TYPE || openModalType === SCHEDULE_MODAL_TYPE_DIALOG}
              onClose={(refreshData) => {
                handleResetGlobalModals(!!inlineDrawer);
                modalCallback(refreshData === true);
              }}
              schedule={selectedSchedule}
              event={selectedScheduleEvent && selectedScheduleEvent.start && selectedScheduleEvent}
              config={scheduleConfig}
            />
          )
        }
        {
          addEditSchedule && (openModalType === SCHEDULE_MODAL_TYPE || openModalType === SCHEDULE_MODAL_TYPE_DIALOG) && scheduleConfig.locationId && (
            <AddEditEventScheduleModal
              open={openModalType === SCHEDULE_MODAL_TYPE || openModalType === SCHEDULE_MODAL_TYPE_DIALOG}
              onClose={(refreshData) => {
                handleResetGlobalModals(!!inlineDrawer);
                modalCallback(refreshData === true);
              }}
              schedule={selectedSchedule}
              event={selectedScheduleEvent && selectedScheduleEvent.start && selectedScheduleEvent}
              config={scheduleConfig}
            />
          )
        }
        {
          horse && selectedBarnHorse && (
            <Box
              sx={[{ height: '100%' }, openModalType === null
                ? { display: 'block' }
                : { display: 'none' }]}
            >
              <HorsePopover
                navigate={navigate}
                onClose={() => { dispatch(setSelectedBarnHorse(null)); }}
                barnHorse={selectedBarnHorse}
                condensed
              />
            </Box>
          )
        }
      </Component>
    </LocalizationProvider>
  );
};

GlobalModals.propTypes = {
  statusChange: PropTypes.bool,
  task: PropTypes.bool,
  horse: PropTypes.bool,
  addEditSchedule: PropTypes.bool,
};

GlobalModals.defaultProps = {
  statusChange: false,
  task: false,
  horse: false,
  addEditSchedule: false,
};

export default GlobalModals;
